import ConnectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import PromotionsCRUDActions from '@mm/state/actions/PromotionsCRUDActions';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import viewActions from '@mm/state/actions/viewActions';
import { isTeamTrial } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
  selectActiveFacebookPageFromSession,
  selectBasicPages,
} from '@mm/state/selectors/facebookPageSelector';
import {
  defaultProUnicornPlanSelector,
  defaultTeamPlanSelector,
  formattedInvoicesSelector,
  has119PlusSubscriptionSelector,
  hasTeamSubscriptionSelector,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  activeCompanySelector,
  companyAdminSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { OVERAGES_ENABLED } from '../../../../common/constants/overagesText';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { useActivateSmsRedirectCallback } from '../../../../common/hooks/use-activate-sms-redirect-callback';
import { useFreeTrialUpgradeModal } from '../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { SettingsRoutes } from '../../../../routes/settings-routes';
import './BillingPanel.pcss';
import { PaidFeatures } from './PaidFeatures/PaidFeatures';
import SubscriptionTabsArea from './SubscriptionTabsArea/SubscriptionTabsArea';

export type BillingPannelContextType = {
  landmineOpenId: number | undefined;
};
export const BillingPannelContext = React.createContext(
  {} as BillingPannelContextType,
);

type OuterProps = {};

type InnerProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  RouterComponentProps;

export type BillingPanelProps = OuterProps & InnerProps;

const BillingPanel = (props: BillingPanelProps) => {
  const {
    facebookPages,
    defaultUnicornPlan,
    defaultTeamPlan,
    hasTeamSubscription,
    accountDetails,
    invoices,
    isCompanyAdmin,
  } = props;
  const dispatch = useDispatch();
  const [searchInput, setSearchInput] = useState('');
  const teamTrial = isTeamTrial(facebookPages);
  const editBillingLink =
    accountDetails && accountDetails.account
      ? accountDetails.account.editBillingLink
      : '';
  const [overagesEnabled, setOveragesEnabled] = useState(false);
  const [landmineOpenId, setLandmineOpenId] = useState<number | undefined>(
    undefined,
  );
  const [landmineStatusUpdated, setLandmineStatusUpdated] = useState(false);
  const reduxViewLandmineOpenId = useSelector(
    (state: StoreState) => state.view.landmineOpenId,
  );
  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();

  const facebookPageFromDetails = useSelector(activeFacebookPageSelector);
  const facebookPageFromSession = useSelector(
    selectActiveFacebookPageFromSession,
  );
  const enhancedPages = useSelector(enhancedBillingPagesSelector);
  const facebookPage =
    facebookPageFromDetails ||
    enhancedPages.find(p => p.id === facebookPageFromSession?.id);

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal(facebookPage);

  const has119PlusSubscription = useSelector(has119PlusSubscriptionSelector);
  const isFreeOrProPlan =
    !facebookPage?.subscription ||
    (facebookPage.subscription.planType === 'pro' &&
      facebookPage.subscription.planSubtypes.includes('pro')) ||
    facebookPage.subscription.planType === 'pro_special';

  const disableUpgradeButton = isFreeTrialPromotionActive;
  // Note: we need to pass ladmineOpenId from view state to the Billing Modal
  // once for the first open after setting it only. That's why we read and reset it.
  useEffect(() => {
    if (reduxViewLandmineOpenId) {
      setLandmineOpenId(reduxViewLandmineOpenId);
      dispatch(viewActions.setLandmineOpenId(undefined));
    }
  }, []);

  useEffect(() => {
    const enableOverages = async () => {
      const {
        location: { search },
      } = props;
      const parsed = queryString.parse(search);
      const facebookPageId =
        typeof parsed.fbPageId === 'string' && parsed.fbPageId;
      const subscriptionId = typeof parsed.subId === 'string' && parsed.subId;
      const { src, landmineOpenId: urlLandmineOpenId } = parsed;
      if (
        urlLandmineOpenId &&
        typeof urlLandmineOpenId === 'string' &&
        urlLandmineOpenId !== 'nil'
      ) {
        setLandmineOpenId(Number(urlLandmineOpenId));
        if (!landmineStatusUpdated) {
          landmineOpensCRUDActions.update(Number(urlLandmineOpenId), true);
          setLandmineStatusUpdated(true);
        }
      }

      if (facebookPageId && subscriptionId && src === 'productUpdates') {
        const { sendOveragesEnabled, openAllSetModal } = props;
        await sendOveragesEnabled(
          parseInt(subscriptionId, 10),
          true,
          parseInt(facebookPageId, 10),
        );

        setOveragesEnabled(true);
        openAllSetModal(OVERAGES_ENABLED);
      }
    };

    if (!overagesEnabled) enableOverages();
  });

  const onSuccessBilling = useActivateSmsRedirectCallback();
  const modalActionsMap = useModalActions();

  const openBillingModal = () => {
    if (defaultTeamPlan) {
      modalActionsMap.openBillingModal({
        plan: defaultTeamPlan,
        landmineOpenId,
        upgradetoTeam: true,
        stayOnPage: false,
        onSuccessBilling,
        facebookPage,
      });
    }
  };

  useEffect(() => {
    const sp = queryString.parse(props.location.search) || {};
    const { fbPageId, src, landmineId } = sp;

    const landmineIdInt = parseInt(landmineId as string, 10);
    const pageId = parseInt(fbPageId as string, 10);

    const page = facebookPages.find(({ id }) => id === pageId);

    if (fbPageId && src === 'trial' && defaultUnicornPlan && page) {
      modalActionsMap.openBillingModal({
        plan: defaultUnicornPlan,
        landmineOpenId,
        stayOnPage: false,
        onSuccessBilling,
        facebookPage: page as EnhancedBillingPage,
      });

      if (landmineId) {
        setLandmineOpenId(landmineIdInt);
        if (!landmineStatusUpdated) {
          landmineOpensCRUDActions.update(landmineIdInt, true);
          setLandmineStatusUpdated(true);
        }
      }
    }
  }, [props.location.search]);

  const contextValue = useMemo(() => {
    return { landmineOpenId };
  }, [landmineOpenId]);

  return (
    <BillingPannelContext.Provider value={contextValue}>
      <div className="billing-panel">
        <div className="settings-pane__title">Pricing Plans</div>
        {isFreeOrProPlan && !has119PlusSubscription && !hasTeamSubscription && (
          <div className="billing-panel__section">
            <PaidFeatures />
          </div>
        )}

        {accountDetails && (
          <div className="billing-panel__section">
            <div className="billing-panel__section-header">
              <div className="billing-panel__header-left">
                <span>Your Subscriptions</span>

                <div className="billing-panel__header-links">
                  {isCompanyAdmin && editBillingLink && (
                    <a
                      href={editBillingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="billing-panel__header-link"
                    >
                      Edit Billing Info
                    </a>
                  )}

                  {isCompanyAdmin && invoices && !!invoices.length && (
                    <Link
                      className="billing-panel__header-link"
                      to={SettingsRoutes.billing.invoices.getLink()}
                    >
                      View Subscription Details
                    </Link>
                  )}
                </div>
              </div>

              {isCompanyAdmin &&
                defaultTeamPlan &&
                !hasTeamSubscription &&
                !teamTrial && (
                  <div className="billing-panel__upgrade-team-section">
                    <div className="billing-panel__upgrade-team-section-text">
                      Need to add more accounts or agents? Let's chat.
                    </div>

                    <PrimaryButton
                      onClick={() =>
                        window.open(
                          'https://calendly.com/d/ch3-rj6-yr6/omnichat-demo-of-mobilemonkey-consultation',
                          '_blank',
                        )
                      }
                      disabled={disableUpgradeButton}
                    >
                      Book a Demo now
                    </PrimaryButton>
                  </div>
                )}
            </div>
            <PrimaryToolTip
              align="end"
              anchorElement={
                <div className="billing-panel__questions-anchor">
                  <i className="far fa-question-circle" />
                  <span className="billing-panel__questions-anchor--text">
                    Questions?
                  </span>
                </div>
              }
            >
              <div>
                <span>Contact us at </span>
                <span>
                  <a href="mailto:support@mobilemonkey.com">
                    support@mobilemonkey.com
                  </a>
                </span>
              </div>
            </PrimaryToolTip>

            <SubscriptionTabsArea
              searchInput={searchInput}
              setSearchInput={setSearchInput}
            />
          </div>
        )}
      </div>
    </BillingPannelContext.Provider>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
  invoices: formattedInvoicesSelector(state),
  facebookPages: selectBasicPages(state),
  defaultTeamPlan: defaultTeamPlanSelector(state),
  defaultUnicornPlan: defaultProUnicornPlanSelector(state),
  accountDetails: selectAccountDetails(state),
  hasTeamSubscription: hasTeamSubscriptionSelector(state),
  isCompanyAdmin: companyAdminSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAccountData: (companyId: number, signal?: AbortSignal) =>
    dispatch(subscriptionCRUDActions.getAccountData(companyId, signal)),
  clearAccountData: () => dispatch(subscriptionCRUDActions.clearAccountData()),
  openAllSetModal: (message: string) =>
    dispatch(modalActions.openAllSetModal({ message })),
  getPages: (companyId: number) =>
    dispatch(
      ConnectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
        withSubscription: true,
        withDowngradePending: true,
        withMessagesStatistic: true,
      }),
    ),
  getPromotionsList: (companyId: number) =>
    dispatch(PromotionsCRUDActions.getPromotions(companyId)),
  sendOveragesEnabled: (
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId?: number,
  ) =>
    dispatch(
      subscriptionCRUDActions.sendOveragesEnabled(
        subscriptionId,
        overagesEnabled,
        facebookPageId,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BillingPanel);
